import { CalendarOutlined } from '@ant-design/icons';
import { Popover } from 'antd';

import { LEADS_PERIOD_PRESETS } from '@app/pages/Leads/LeadsRegistryPage/model/leadsDatePresets';
import type {
  LeadsPeriodPresetId,
  LeadsRegistryDateRange,
} from '@app/pages/Leads/LeadsRegistryPage/model/types';
import { PeriodPicker } from '@app/shared/components/PeriodPicker';

import { useLeadsPeriodPicker } from './hooks/useLeadsPeriodPicker';
import { formatLeadsPeriodLabel } from './lib/formatLeadsPeriodLabel';

import type { FC } from 'react';

import './LeadsPeriodPicker.scss';

const PF = 'LeadsPeriodPicker';

interface LeadsPeriodPickerProps {
  dates: LeadsRegistryDateRange;
  activePresetId: LeadsPeriodPresetId | null;
  onDatesChange(range: LeadsRegistryDateRange): void;
  onDatePresetChange(id: LeadsPeriodPresetId): void;
}

export const LeadsPeriodPicker: FC<LeadsPeriodPickerProps> = ({
  dates,
  activePresetId,
  onDatesChange,
  onDatePresetChange,
}) => {
  const {
    isOpen,
    viewMonth,
    selectionPhase,
    hasDraft,
    triggerDates,
    shownStart,
    shownEnd,
    previewStart,
    previewEnd,
    onOpenChange,
    onDayClick,
    onDayHover,
    onViewMonthChange,
    onPresetSelect,
    onResetPeriod,
    onSave,
  } = useLeadsPeriodPicker({ dates, onDatesChange, onDatePresetChange });

  const panel = (
    <PeriodPicker
      presets={LEADS_PERIOD_PRESETS}
      activePresetId={hasDraft ? null : activePresetId}
      viewMonth={viewMonth}
      startDate={shownStart}
      endDate={shownEnd}
      isSelecting={selectionPhase === 'selecting'}
      previewStart={previewStart}
      previewEnd={previewEnd}
      hint={
        activePresetId && !hasDraft
          ? 'Период обновляется автоматически при смене дня'
          : undefined
      }
      isSaveDisabled={!hasDraft}
      onPresetSelect={onPresetSelect}
      onDayClick={onDayClick}
      onDayHover={onDayHover}
      onViewMonthChange={onViewMonthChange}
      onReset={onResetPeriod}
      onSave={onSave}
    />
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={onOpenChange}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      overlayClassName={`${PF}__overlay`}
      content={panel}
    >
      <button
        type="button"
        className={`${PF}__trigger`}
        data-testid="leads-period-filter"
      >
        <span className={`${PF}__trigger-value`}>
          {formatLeadsPeriodLabel(triggerDates)}
        </span>
        <CalendarOutlined className={`${PF}__trigger-icon`} />
      </button>
    </Popover>
  );
};
